/*
 *==================================================
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2021
 *
 *==================================================
 */
//Standard libraries
import React from "react";
import Grid from "@material-ui/core/Grid";
import { Typography, Divider, Switch } from "@material-ui/core";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { useTranslation } from "react-i18next";

//custom library
import OrderItemsTable from "./order-items-table";
import { Paper } from "../components";
import {
  dollarFormat,
  onlyDateFormat,
  timeFormat,
} from "../helpers/dataFormat";

interface OrderDetailSectionProps {
  orderDetail: any;
  orderItem: any[];
  getPrevious: Function;
  getNext: Function;
}

const OrderDetailSection: React.FC<OrderDetailSectionProps> = ({
  orderDetail,
  orderItem,
  getPrevious,
  getNext,
}) => {
  const [showOrderItems, setShowOrderItems] = React.useState(false);
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const currency = orderDetail.grandTotalCurrency;

  return (
    <Paper>
      <Grid container>
        <Grid item xs={8}>
          <Grid item>
            <Typography variant="h2">
              {t("OrderManagement.OrderDetails")}
            </Typography>
          </Grid>
          <Grid item>
            <FormControlLabel
              control={
                <Switch
                  checked={!!showOrderItems}
                  onChange={() => {
                    setShowOrderItems(!showOrderItems);
                  }}
                  name="switchToShow"
                  color="primary"
                />
              }
              label={
                !showOrderItems
                  ? t("OrderManagement.ShowOrderItemTable")
                  : t("OrderManagement.HideOrderItemTable")
              }
            />
          </Grid>
          <Grid item container direction="row" spacing={1}>
            <Grid item>
              <Typography variant="body2">
                {t("OrderManagement.OrderDate")}:
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1">
                {orderDetail.lastUpdateDate &&
                  `${onlyDateFormat(locale, orderDetail.lastUpdateDate)}`}
              </Typography>
            </Grid>
          </Grid>
          <Grid item container direction="row" spacing={1}>
            <Grid item>
              <Typography variant="body2">
                {t("OrderManagement.OrderTime")}:
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1">
                {orderDetail.lastUpdateDate &&
                  `${timeFormat(locale, orderDetail.lastUpdateDate)}`}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={4}>
          <table style={{ width: "100%" }}>
            <tbody>
              {orderDetail.totalProductPrice && (
                <tr>
                  <td>
                    <Typography variant="body1">
                      {t("OrderManagement.OrderSubtotal")}
                    </Typography>
                  </td>
                  <td>
                    <Typography variant="body1" align="right">
                      {currency &&
                        dollarFormat(
                          Number(orderDetail.totalProductPrice),
                          locale,
                          currency
                        )}
                    </Typography>
                  </td>
                </tr>
              )}
              {orderDetail.totalAdjustment !== null && (
                <tr>
                  <td>
                    <Typography variant="body1">
                      {t("OrderManagement.Discounts")}
                    </Typography>
                  </td>
                  <td>
                    <Typography variant="body1" align="right">
                      {currency &&
                        dollarFormat(
                          Number(orderDetail.totalAdjustment),
                          locale,
                          currency
                        )}
                    </Typography>
                  </td>
                </tr>
              )}
              {orderDetail.totalSalesTax !== null && (
                <tr>
                  <td>
                    <Typography variant="body1">
                      {t("OrderManagement.Tax")}
                    </Typography>
                  </td>
                  <td>
                    <Typography variant="body1" align="right">
                      {currency &&
                        dollarFormat(
                          Number(orderDetail.totalSalesTax),
                          locale,
                          currency
                        )}
                    </Typography>
                  </td>
                </tr>
              )}
              {orderDetail.totalShippingCharge !== null && (
                <tr>
                  <td>
                    <Typography variant="body1">
                      {t("OrderManagement.Shipping")}
                    </Typography>
                  </td>
                  <td>
                    <Typography variant="body1" align="right">
                      {currency &&
                        dollarFormat(
                          Number(orderDetail.totalShippingCharge),
                          locale,
                          currency
                        )}
                    </Typography>
                  </td>
                </tr>
              )}
              {orderDetail.grandTotal && (
                <tr>
                  <td>
                    <Typography variant="body1">
                      {t("OrderManagement.OrderTotal")}
                    </Typography>
                  </td>
                  <td>
                    <Typography variant="body1" align="right">
                      {currency &&
                        dollarFormat(orderDetail.grandTotal, locale, currency)}
                    </Typography>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Grid>
      </Grid>
      {showOrderItems && (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Divider />
          </Grid>
          <Grid item>
            <Typography variant="h3" component="p">
              {t("OrderManagement.OrderItemDetail")}
            </Typography>
          </Grid>
          <Grid item>
            <OrderItemsTable
              {...{
                orderDetail,
                orderItem,
                getPrevious,
                getNext,
              }}
            />
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

export default OrderDetailSection;
